import { forwardRef } from 'react';
import type {
  ConfidenceBand,
  MetricStatus,
  ProductHealth,
  SourceMode,
} from '@aurora-pvi/contracts';
import { Badge, type BadgeProps, type BadgeTone } from '../primitives/Badge';
import { Tooltip } from '../primitives/Tooltip';
import type { IconName } from '../primitives/Icon';
import { formatRelativeTime } from '../utils/format';
import { cn } from '../utils/cn';

/**
 * The three trust badges. Each pairs an icon and a word with its tone, so that
 * removing colour entirely still leaves the status legible — the accessibility
 * contract forbids colour as the only carrier of meaning.
 */

// ── SourceBadge ─────────────────────────────────────────────────────────────

const sourceModeMeta: Record<SourceMode, { label: string; icon: IconName }> = {
  FileFeed: { label: 'File feed', icon: 'upload' },
  Manual: { label: 'Manual entry', icon: 'edit' },
  Calculated: { label: 'Calculated', icon: 'activity' },
  DirectConnector: { label: 'Connected', icon: 'plug' },
};

const statusMeta: Record<MetricStatus, { label: string; icon: IconName; tone: BadgeTone }> = {
  Available: { label: 'Current', icon: 'check-circle', tone: 'success' },
  Partial: { label: 'Partial', icon: 'alert-circle', tone: 'warning' },
  Stale: { label: 'Stale', icon: 'clock', tone: 'attention' },
  Unavailable: { label: 'Unavailable', icon: 'x-circle', tone: 'neutral' },
  Error: { label: 'Source failed', icon: 'alert', tone: 'danger' },
};

export interface SourceBadgeProps extends Omit<BadgeProps, 'tone' | 'icon' | 'children'> {
  sourceMode: SourceMode;
  status?: MetricStatus;
  sourceSystem?: string | null;
  refreshedAt?: string | null;
  /** Show the source system instead of the mode when there is room. */
  showSystem?: boolean;
}

export const SourceBadge = forwardRef<HTMLSpanElement, SourceBadgeProps>(function SourceBadge(
  { sourceMode, status = 'Available', sourceSystem, refreshedAt, showSystem = true, ...rest },
  ref,
) {
  const mode = sourceModeMeta[sourceMode];
  const state = statusMeta[status];
  const label = showSystem && sourceSystem ? sourceSystem : mode.label;
  const tone: BadgeTone = status === 'Available' ? 'neutral' : state.tone;

  return (
    <Tooltip
      content={
        <span>
          {mode.label}
          {sourceSystem ? ` · ${sourceSystem}` : ''} · {state.label} · refreshed{' '}
          {formatRelativeTime(refreshedAt ?? null)}
        </span>
      }
    >
      <Badge
        ref={ref}
        tone={tone}
        icon={status === 'Available' ? mode.icon : state.icon}
        {...rest}
        tabIndex={0}
      >
        {label}
      </Badge>
    </Tooltip>
  );
});

// ── ConfidenceBadge ─────────────────────────────────────────────────────────

const confidenceMeta: Record<
  ConfidenceBand,
  { icon: IconName; tone: BadgeTone; bars: number; explanation: string }
> = {
  High: {
    icon: 'check-circle',
    tone: 'success',
    bars: 3,
    explanation: 'Derived from a system of record or approved by governance.',
  },
  Medium: {
    icon: 'alert-circle',
    tone: 'warning',
    bars: 2,
    explanation: 'Rule-derived or user-asserted; not yet specialist reviewed.',
  },
  Low: {
    icon: 'alert',
    tone: 'attention',
    bars: 1,
    explanation: 'Assisted candidate or incomplete evidence. Treat as indicative.',
  },
  Unknown: {
    icon: 'help',
    tone: 'neutral',
    bars: 0,
    explanation: 'No confidence has been recorded for this value.',
  },
};

export interface ConfidenceBadgeProps extends Omit<BadgeProps, 'tone' | 'icon' | 'children'> {
  band: ConfidenceBand;
  method?: string | null;
  /** Adds a three-bar meter alongside the word for at-a-glance scanning. */
  showMeter?: boolean;
}

export const ConfidenceBadge = forwardRef<HTMLSpanElement, ConfidenceBadgeProps>(
  function ConfidenceBadge({ band, method, showMeter = false, className, ...rest }, ref) {
    const meta = confidenceMeta[band];
    return (
      <Tooltip
        content={<span>{method ? `${method}. ${meta.explanation}` : meta.explanation}</span>}
      >
        <Badge
          ref={ref}
          tone={meta.tone}
          icon={meta.icon}
          className={cn('gap-1.5', className)}
          tabIndex={0}
          {...rest}
        >
          {band} confidence
          {showMeter ? (
            <span aria-hidden="true" className="ml-0.5 inline-flex items-end gap-px">
              {[1, 2, 3].map((bar) => (
                <span
                  key={bar}
                  className={cn(
                    'w-1 rounded-[1px]',
                    bar === 1 ? 'h-1.5' : bar === 2 ? 'h-2' : 'h-2.5',
                    bar <= meta.bars ? 'bg-current' : 'bg-current opacity-25',
                  )}
                />
              ))}
            </span>
          ) : null}
        </Badge>
      </Tooltip>
    );
  },
);

// ── HealthBadge ─────────────────────────────────────────────────────────────

const healthMeta: Record<ProductHealth, { label: string; icon: IconName; tone: BadgeTone }> = {
  Healthy: { label: 'Healthy', icon: 'check-circle', tone: 'success' },
  AtRisk: { label: 'At risk', icon: 'alert-circle', tone: 'warning' },
  HighRisk: { label: 'High risk', icon: 'alert', tone: 'attention' },
  Critical: { label: 'Critical', icon: 'x-circle', tone: 'danger' },
  Unknown: { label: 'Not assessed', icon: 'help', tone: 'neutral' },
};

export interface HealthBadgeProps extends Omit<BadgeProps, 'tone' | 'icon' | 'children'> {
  health: ProductHealth;
  /** Health score 0–100, rendered next to the word when supplied. */
  score?: number | null;
  reason?: string | null;
}

export const HealthBadge = forwardRef<HTMLSpanElement, HealthBadgeProps>(function HealthBadge(
  { health, score, reason, ...rest },
  ref,
) {
  const meta = healthMeta[health];
  const badge = (
    <Badge ref={ref} tone={meta.tone} icon={meta.icon} tabIndex={reason ? 0 : undefined} {...rest}>
      {meta.label}
      {score !== null && score !== undefined ? ` · ${Math.round(score)}` : ''}
    </Badge>
  );
  return reason ? <Tooltip content={reason}>{badge}</Tooltip> : badge;
});

// ── SyntheticDataBadge ──────────────────────────────────────────────────────

export interface SyntheticDataBadgeProps extends Omit<BadgeProps, 'tone' | 'icon' | 'children'> {
  /** Where the illustrative data came from, e.g. 'Aurora seed set v3'. */
  datasetLabel?: string | null;
}

/**
 * Seed data must be labelled wherever it appears, so this badge is intentionally
 * loud: attention orange, an explicit word, and a tooltip that says what it is.
 */
export const SyntheticDataBadge = forwardRef<HTMLSpanElement, SyntheticDataBadgeProps>(
  function SyntheticDataBadge({ datasetLabel, ...rest }, ref) {
    return (
      <Tooltip
        content={`Illustrative data${
          datasetLabel ? ` from ${datasetLabel}` : ''
        }. Not sourced from a system of record — do not use for decisions.`}
      >
        <Badge ref={ref} tone="attention" icon="flag" tabIndex={0} {...rest}>
          Synthetic data
        </Badge>
      </Tooltip>
    );
  },
);
